// src/context/SettingsContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import SettingsApi from '../api-services/settings-api.service';
import { Settings } from '../interfaces/settings.interface';
import { isNewLayoutVersion } from '../utils/helpers';
import useBreakpoint from '../utils/useBreakpoint';

interface SettingsContextType {
  settings: Settings | null;
  loading: boolean;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);

  // Load app-wide settings once on startup. These drive, among other things,
  // which landing page layout is shown (see HomepageSwitcher). On failure we
  // leave settings null so consumers fall back to the legacy behaviour.
  useEffect(() => {
    let active = true;

    SettingsApi.getSettings()
      .then(data => {
        if (active) setSettings(data);
      })
      .catch(error => {
        console.error('Failed to load settings.', error);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  return (
    <SettingsContext.Provider value={{ settings, loading }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

// Single source of truth for "should the redesigned (v2) UI be shown?". Desktop
// and mobile are gated independently: the shared useBreakpoint() threshold
// decides which version field applies, and a version >= 2 selects v2. While
// settings are still loading or the request failed this returns false, so the
// site never renders worse than the current design. Used by the homepage,
// header and footer switchers.
export const useIsNewLayout = (): boolean => {
  const { settings } = useSettings();
  const isMobile = useBreakpoint();

  const version = isMobile
    ? settings?.b2c2b_mobile_version
    : settings?.b2c2b_desktop_version;

  return isNewLayoutVersion(version);
};
